
import React, { useState, useMemo } from 'react';
import { Task, MetricKey, DailySnapshot } from '../types';
import { analyticsService } from '../services/analyticsService';
import { X, Target, TrendingUp, Activity, TrendingDown, Minus } from 'lucide-react';

const METRIC_CONFIG: Record<MetricKey, { title: string; unit: string; color: string; stroke: string; fill: string; icon: React.FC<any>; description: string }> = {
  prioriScore: {
    title: 'Clarity Score',
    unit: 'PTS',
    color: 'blue',
    stroke: '#3b82f6',
    fill: '#3b82f6',
    icon: Target,
    description: 'Cumulative weighted completion points. Q1 tasks score highest.'
  },
  questVelocity: {
    title: 'Completion Rate',
    unit: '%',
    color: 'emerald',
    stroke: '#10b981',
    fill: '#10b981',
    icon: TrendingUp,
    description: 'Rolling 7-day task completion ratio.'
  },
  deepWorkRatio: {
    title: 'Deep Work Ratio',
    unit: '%',
    color: 'amber',
    stroke: '#f59e0b',
    fill: '#f59e0b',
    icon: Activity,
    description: 'Percentage of tasks in Q2 (Strategic) quadrant.'
  }
};

interface Props {
  metricKey: MetricKey;
  tasks: Task[];
  onClose: () => void;
}

export const MetricHistoryModal: React.FC<Props> = ({ metricKey, tasks, onClose }) => {
  const [days, setDays] = useState<30 | 60>(30);
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);

  const config = METRIC_CONFIG[metricKey];
  const Icon = config.icon;

  const snapshots = useMemo(
    () => analyticsService.computeHistoricalSnapshots(tasks, metricKey, days),
    [tasks, metricKey, days]
  );

  const values = snapshots.map(s => s.value);
  const minVal = Math.min(...values);
  const maxVal = Math.max(...values);
  const avgVal = values.length > 0 ? Math.round(values.reduce((a, b) => a + b, 0) / values.length) : 0;
  const currentVal = values[values.length - 1] || 0;
  const prevVal = values[values.length - 2] || 0;
  const trend = currentVal > prevVal ? 'up' : currentVal < prevVal ? 'down' : 'flat';
  const isEmpty = values.every(v => v === 0);

  // SVG dimensions
  const W = 560;
  const H = 240;
  const PAD_L = 45;
  const PAD_R = 15;
  const PAD_T = 20;
  const PAD_B = 35;
  const chartW = W - PAD_L - PAD_R;
  const chartH = H - PAD_T - PAD_B;

  const range = maxVal - minVal || 1;
  const xScale = (i: number) => PAD_L + (i / Math.max(1, snapshots.length - 1)) * chartW;
  const yScale = (v: number) => PAD_T + chartH - ((v - minVal) / range) * chartH;

  const linePoints = snapshots.map((s, i) => `${xScale(i)},${yScale(s.value)}`).join(' ');
  const areaPoints = `${xScale(0)},${PAD_T + chartH} ${linePoints} ${xScale(snapshots.length - 1)},${PAD_T + chartH}`;

  // Y-axis ticks (4 levels)
  const yTicks = [0, 1, 2, 3].map(i => {
    const val = minVal + (range * i) / 3;
    return { val: Math.round(val), y: yScale(val) };
  });

  // X-axis labels (every 7th day)
  const xLabels = snapshots
    .map((s, i) => ({ label: new Date(s.timestamp).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }), x: xScale(i), i }))
    .filter((_, i) => i % 7 === 0 || i === snapshots.length - 1);

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm animate-in fade-in duration-200" onClick={onClose}>
      <div className="w-full max-w-2xl bg-white rounded-2xl shadow-2xl overflow-hidden animate-in zoom-in-95 duration-200" onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div className="px-6 py-5 border-b border-slate-100 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className={`p-2.5 bg-${config.color}-50 text-${config.color}-600 rounded-xl`}>
              <Icon size={20} />
            </div>
            <div>
              <h3 className="text-lg font-bold text-slate-900">{config.title}</h3>
              <p className="text-xs text-slate-500">{config.description}</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-full transition-colors text-slate-400"><X size={20} /></button>
        </div>

        {/* Period Toggle */}
        <div className="px-6 pt-4 flex items-center gap-2">
          {([30, 60] as const).map(d => (
            <button
              key={d}
              onClick={() => setDays(d)}
              className={`px-4 py-1.5 rounded-lg text-xs font-bold transition-all ${days === d ? 'bg-slate-900 text-white' : 'bg-slate-100 text-slate-500 hover:bg-slate-200'}`}
            >
              {d} Days
            </button>
          ))}
        </div>

        {/* Chart */}
        <div className="px-6 py-4">
          {isEmpty ? (
            <div className="h-60 flex items-center justify-center text-slate-400">
              <div className="text-center space-y-2">
                <Activity size={32} className="mx-auto opacity-30" />
                <p className="text-sm font-medium">Not enough data yet</p>
                <p className="text-xs">Complete tasks to see your trend over time.</p>
              </div>
            </div>
          ) : (
            <div className="relative">
              <svg viewBox={`0 0 ${W} ${H}`} width="100%" className="overflow-visible">
                {/* Grid lines */}
                {yTicks.map((tick, i) => (
                  <g key={i}>
                    <line x1={PAD_L} y1={tick.y} x2={W - PAD_R} y2={tick.y} stroke="#e2e8f0" strokeWidth="1" strokeDasharray={i === 0 ? "0" : "4 4"} />
                    <text x={PAD_L - 8} y={tick.y + 4} textAnchor="end" fill="#94a3b8" fontSize="10" fontFamily="monospace">{tick.val}</text>
                  </g>
                ))}

                {/* X-axis labels */}
                {xLabels.map((label, i) => (
                  <text key={i} x={label.x} y={H - 5} textAnchor="middle" fill="#94a3b8" fontSize="9" fontFamily="monospace">{label.label}</text>
                ))}

                {/* Area fill */}
                <defs>
                  <linearGradient id={`grad-${metricKey}`} x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={config.fill} stopOpacity="0.2" />
                    <stop offset="100%" stopColor={config.fill} stopOpacity="0.02" />
                  </linearGradient>
                </defs>
                <polygon points={areaPoints} fill={`url(#grad-${metricKey})`} />

                {/* Line */}
                <polyline points={linePoints} fill="none" stroke={config.stroke} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />

                {/* Data point circles */}
                {snapshots.map((s, i) => (
                  <circle
                    key={i}
                    cx={xScale(i)}
                    cy={yScale(s.value)}
                    r={hoveredIndex === i ? 5 : 0}
                    fill={config.stroke}
                    stroke="white"
                    strokeWidth="2"
                    className="transition-all duration-150"
                  />
                ))}

                {/* Invisible hit targets for hover */}
                {snapshots.map((s, i) => (
                  <rect
                    key={`hit-${i}`}
                    x={xScale(i) - chartW / snapshots.length / 2}
                    y={PAD_T}
                    width={chartW / snapshots.length}
                    height={chartH}
                    fill="transparent"
                    onMouseEnter={() => setHoveredIndex(i)}
                    onMouseLeave={() => setHoveredIndex(null)}
                    onTouchStart={() => setHoveredIndex(i)}
                    onTouchEnd={() => setHoveredIndex(null)}
                  />
                ))}
              </svg>

              {/* Tooltip */}
              {hoveredIndex !== null && snapshots[hoveredIndex] && (
                <div
                  className="absolute pointer-events-none bg-slate-900 text-white px-3 py-1.5 rounded-lg shadow-lg text-xs font-medium whitespace-nowrap"
                  style={{
                    left: `${(xScale(hoveredIndex) / W) * 100}%`,
                    top: `${(yScale(snapshots[hoveredIndex].value) / H) * 100 - 15}%`,
                    transform: 'translate(-50%, -100%)'
                  }}
                >
                  <span className="font-bold">{snapshots[hoveredIndex].value} {config.unit}</span>
                  <span className="text-slate-400 ml-2">{new Date(snapshots[hoveredIndex].timestamp).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}</span>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Summary Row */}
        {!isEmpty && (
          <div className="px-6 pb-5 grid grid-cols-4 gap-4">
            <div className="bg-slate-50 rounded-xl p-3 text-center">
              <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Current</p>
              <p className="text-lg font-bold text-slate-900">{currentVal}<span className="text-xs text-slate-400 ml-0.5">{config.unit}</span></p>
            </div>
            <div className="bg-slate-50 rounded-xl p-3 text-center">
              <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Average</p>
              <p className="text-lg font-bold text-slate-900">{avgVal}<span className="text-xs text-slate-400 ml-0.5">{config.unit}</span></p>
            </div>
            <div className="bg-slate-50 rounded-xl p-3 text-center">
              <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Peak</p>
              <p className="text-lg font-bold text-slate-900">{maxVal}<span className="text-xs text-slate-400 ml-0.5">{config.unit}</span></p>
            </div>
            <div className="bg-slate-50 rounded-xl p-3 text-center">
              <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Trend</p>
              <div className="flex items-center justify-center gap-1 mt-1">
                {trend === 'up' && <TrendingUp size={18} className="text-emerald-500" />}
                {trend === 'down' && <TrendingDown size={18} className="text-rose-500" />}
                {trend === 'flat' && <Minus size={18} className="text-slate-400" />}
                <span className={`text-sm font-bold ${trend === 'up' ? 'text-emerald-500' : trend === 'down' ? 'text-rose-500' : 'text-slate-400'}`}>
                  {trend === 'up' ? 'Rising' : trend === 'down' ? 'Falling' : 'Steady'}
                </span>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
